/**
 * API-Client fuer das Uebertragungsprotokoll (41-05/41-06): Gesamtuebersicht, gefilterte Liste,
 * Detail samt Beleg, CSV aus dem Beleg und der Link-Zeilen-Zustand eines einzelnen Zeitraums.
 *
 * Nutzt ausschliesslich den gemeinsamen `api`-Client (`services/api.ts`), Muster 1:1 von
 * `evaluationTableApi.ts` (`getTable`/`downloadCsv`) uebernommen.
 */
import { api } from './api';
import type {
  TransferDetail,
  TransferListResponse,
  TransferOverviewQuery,
  TransferOverviewResponse,
  TransferLinkState,
} from '../types/statistikPortalTransfer';

const BASE_PATH = '/api/statistik-portal/transfers';

/**
 * Laesst `null`/`undefined` weg, damit sie nicht als Query-Parameter gesendet werden. Nimmt
 * bewusst `object` statt eines `Record`-Typs mit Indexsignatur entgegen -- `TransferOverviewQuery`
 * ist ein festes Interface ohne Indexsignatur.
 */
function toParams(values: object): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  for (const [key, value] of Object.entries(values)) {
    if (value !== null && value !== undefined) {
      params[key] = value as string | number;
    }
  }
  return params;
}

async function getOverview(
  query: Pick<TransferOverviewQuery, 'year' | 'area' | 'type'>
): Promise<TransferOverviewResponse> {
  const response = await api.get<TransferOverviewResponse>(`${BASE_PATH}/overview`, {
    params: toParams(query),
  });
  return response.data;
}

async function getEntries(
  query: TransferOverviewQuery
): Promise<TransferListResponse> {
  const response = await api.get<TransferListResponse>(BASE_PATH, {
    params: toParams(query),
  });
  return response.data;
}

async function getEntry(id: number): Promise<TransferDetail> {
  const response = await api.get<TransferDetail>(`${BASE_PATH}/${id}`);
  return response.data;
}

function filenameFromContentDisposition(
  header: string | undefined
): string | null {
  if (!header) {
    return null;
  }
  const match = /filename="?([^";]+)"?/.exec(header);
  return match ? match[1] : null;
}

/** Dateiname-Rueckfall, falls der Server keinen `content-disposition`-Header liefert. */
function fallbackCsvFilename(id: number): string {
  return `Uebertragung_${id}.csv`;
}

async function downloadEntryCsv(
  id: number
): Promise<{ blob: Blob; filename: string }> {
  const response = await api.get<Blob>(`${BASE_PATH}/${id}/csv`, {
    responseType: 'blob',
  });
  const filename =
    filenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined
    ) ?? fallbackCsvFilename(id);
  return { blob: response.data, filename };
}

async function getTransferState(
  evaluationType: string,
  year: number,
  period: number
): Promise<TransferLinkState> {
  const response = await api.get<TransferLinkState>(
    '/api/statistik-portal/transfer-state',
    { params: { evaluationType, year, period } }
  );
  return response.data;
}

export const statistikPortalTransferApi = {
  getOverview,
  getEntries,
  getEntry,
  downloadEntryCsv,
  getTransferState,
};
